/** Full-file audio profiles, edited for broad service/video use. BPM is estimated. */
export const UPPBEAT_MUSIC_TRACKS = [
    {
        id: 'clean-cut',
        name: 'Clean Cut',
        artist: 'All Good Folks',
        path: '/music/uppbeat/clean-cut.mp3',
        duration: 85.107,
        bpm: 120,
        mood: 'uplifting and confident',
        profile: {
            description: 'Clean Cut brings a polished electronic groove with a steady, moderately brisk pulse and a confident, promotional feel. Its clean texture suits sharp edits for hair styling, beauty appointments, nail designs, and fitness sessions, especially when the finished result leads the story. Use it for transformation reveals, short promotions, or a punchy Step by Step sequence. For educational voiceover, choose a less busy passage and lower the music so the spoken explanation stays clear.',
            mood: 'uplifting and confident',
            genre: 'electronic pop/dance',
            energy: 'medium',
            vocals: 'lyrics',
            voiceoverFit: 'moderate, some sections might conflict with voiceovers',
            serviceUses: [
                'Hair styling',
                'Beauty services',
                'Nail services',
                'Fitness sessions',
            ],
            videoUses: ['Transformation/reveal', 'Promotions', 'Step by Step'],
            editingNotes: 'Consider ducking the volume slightly under voiceover sections. The intro can be trimmed for quicker start.',
            recommendedStartSec: 5.0,
        },
        audioSha256: '035361eba71c1691198515cf0eec749e50c0cf665553e08383e10d3bb615a64c',
        sourceUrl: 'https://uppbeat.io/music/tracks/all-good-folks/clean-cut',
        storage: 'org',
    },
    {
        id: 'ocean-breeze',
        name: 'Ocean Breeze',
        artist: 'Moire',
        path: '/music/uppbeat/ocean-breeze.mp3',
        duration: 111.778,
        bpm: 128,
        mood: 'uplifting and energetic',
        profile: {
            description: 'Ocean Breeze sits in a skincare-oriented library setting, but its rhythmic electronic character may feel more lively than the soft title suggests. A clean, regular pulse gives it forward motion, making it worth trying with facial preparation, skincare application, beauty routines, or upbeat wellness studio footage. Use it for a Skin Edit, a brisk Step by Step, or a behind-the-scenes montage. Check the chosen passage against your pacing, and keep it low beneath educational voiceover rather than assuming a deeply restful backdrop.',
            mood: 'uplifting and energetic',
            genre: 'electronic dance',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: "The track's consistent rhythm and unobtrusive melody make it suitable as a backing bed for voiceovers.",
            serviceUses: [
                'Facials and skincare',
                'Beauty routines',
                'Wellness studio services',
            ],
            videoUses: [
                'Skin Edit',
                'Step by Step',
                'Behind-the-scenes montage',
                'Educational voiceover',
            ],
            editingNotes: "Consider using quick cuts and transitions to match the beat. The track's structure lends itself to highlight reels and energetic sequences.",
            recommendedStartSec: 0,
        },
        audioSha256: 'd06c9f47427e705d3f5fb40b808bfc1cce4e569f875252d417e433baebc80131',
        sourceUrl: 'https://uppbeat.io/music/tracks/moire/ocean-breeze',
        storage: 'org',
    },
    {
        id: 'golden-touch',
        name: 'Golden Touch',
        artist: 'Stan Town',
        path: '/music/uppbeat/golden-touch.mp3',
        duration: 113.006,
        bpm: 118,
        mood: 'uplifting and playful',
        profile: {
            description: 'Golden Touch has a playful, stylish groove with bright electronic textures and an easy, medium-paced bounce. It feels upbeat without needing the urgency of a hard-selling promo, making it a natural option for nail art, hair finishing, makeup, and beauty product demonstrations. Try it with a Step by Step that follows small details, a polished transformation reveal, or a promotion showcasing a seasonal look. Its regular rhythm also supports light educational voiceover when the music is balanced beneath speech.',
            mood: 'uplifting and playful',
            genre: 'electronic pop / tropical house',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'The instrumental nature and steady rhythm make it suitable for voiceover integration.',
            serviceUses: [
                'Nail art',
                'Hair finishing',
                'Makeup',
                'Beauty product demonstrations',
            ],
            videoUses: [
                'Step by Step',
                'Transformation/reveal',
                'Seasonal promotions',
                'Educational voiceover',
            ],
            editingNotes: 'Use the rhythmic sections to time cuts; loop sections easily due to consistent beat; fade in/out works well.',
            recommendedStartSec: 0,
        },
        audioSha256: 'bb2ac787f9325390a67ff6411baf447a3db79932765933f26ae5b9b5d0906401',
        sourceUrl: 'https://uppbeat.io/music/tracks/stan-town/golden-touch',
        storage: 'org',
    },
    {
        id: 'polarity',
        name: 'Polarity',
        artist: 'Prigida',
        path: '/music/uppbeat/polarity.mp3',
        duration: 167.576,
        bpm: 120,
        mood: 'uplifting and confident',
        profile: {
            description: 'Polarity pairs a retro electronic feel with bright, layered textures and a strong, energetic pulse. The mood is confident and forward-moving, suited to fitness studio highlights, bold hair transformations, modern beauty services, or an introduction to a clinical or dental practice. Use the rhythm to structure fast Step by Step edits, equipment demonstrations, promotional reels, and decisive reveals. It can also sit beneath a concise educational voiceover, provided the busier moments do not compete with important spoken details.',
            mood: 'uplifting and confident',
            genre: 'electronic pop',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'The track’s steady beat and clear instrumental arrangement make it suitable for voiceover.',
            serviceUses: [
                'Fitness studios',
                'Hair services',
                'Beauty services',
                'Clinical and dental practices',
            ],
            videoUses: [
                'Step by Step',
                'Equipment demonstrations',
                'Promotions',
                'Transformation/reveal',
                'Educational voiceover',
            ],
            editingNotes: 'Use the beat drops to emphasize key moments in your video. The consistent rhythm makes it easy to loop or fade.',
            recommendedStartSec: 0,
        },
        audioSha256: 'b64be286414191e5128a4d552b4bd994a162953a629005ad595978be1c2b9717',
        sourceUrl: 'https://uppbeat.io/music/tracks/prigida/polarity',
        storage: 'org',
    },
    {
        id: 'summertime',
        name: 'Summertime',
        artist: 'Moire',
        path: '/music/uppbeat/summertime.mp3',
        duration: 112.039,
        bpm: 80,
        mood: 'calm and atmospheric',
        profile: {
            description: 'Summertime offers a warm, laid-back electronic backdrop with a gentle, steady groove and spacious atmosphere. Its unhurried feel suits massage and wellness settings, facial appointments, relaxed hair salon footage, and everyday studio storytelling. Choose it for a Journey that follows arrival through finishing touches, a softly paced Skin Edit, or an educational voiceover explaining an appointment. Smooth transitions and lingering detail shots fit better than rapid cuts, while understated promotions can use the same easygoing mood without feeling overly sales-focused.',
            mood: 'calm and atmospheric',
            genre: 'lo-fi electronic / downtempo',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'The instrumental nature and consistent rhythm make it suitable for voiceover work without distracting.',
            serviceUses: ['Massage', 'Wellness services', 'Facials', 'Hair salons'],
            videoUses: [
                'Journey',
                'Skin Edit',
                'Educational voiceover',
                'Studio storytelling',
                'Understated promotions',
            ],
            editingNotes: 'Use smooth transitions and fades to match the steady rhythm. The track’s consistent beat provides flexibility for looping or trimming.',
            recommendedStartSec: 0,
        },
        audioSha256: '0e791031ca32175ea01ad6bd2d1fa4cf95cb818da746315fc2e219a3dce7b257',
        sourceUrl: 'https://uppbeat.io/music/tracks/moire/summertime',
        storage: 'org',
    },
    {
        id: 'backbay-lounge',
        name: 'Backbay Lounge',
        artist: 'Kevin MacLeod',
        path: '/music/uppbeat/backbay-lounge.mp3',
        duration: 266.58,
        bpm: 90,
        mood: 'calm and mellow',
        profile: {
            description: 'Warm, smooth lounge textures and an unhurried groove give Backbay Lounge a polished, welcoming feel. Its mellow energy suits consultation scenes, facial appointments, hair salon introductions, or a relaxed look around a dental practice. Use it for a Journey that follows arrival through finishing touches, or beneath an educational voiceover where the music should stay in the background. Soft transitions and comfortably paced cuts match its easy flow, making it a useful choice for personable service introductions rather than dramatic reveals.',
            mood: 'calm and mellow',
            genre: 'jazz',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'works well under voiceover due to minimal instrumentation and clear space in the mix',
            serviceUses: [
                'Facials and skincare',
                'Hair salon consultations',
                'Dental practice introductions',
                'Beauty consultations',
            ],
            videoUses: [
                'Journey',
                'Educational voiceover',
                'Service introductions',
                'Behind-the-scenes tours',
            ],
            editingNotes: 'Fade in smoothly, keep transitions soft, and maintain consistent volume levels. The track can loop seamlessly if needed.',
            recommendedStartSec: 0,
        },
        audioSha256: '1982c37e56435dfa9fde9d5e47f9b9e8758d32f824fe30466c8fb0f990d053d8',
        sourceUrl: 'https://uppbeat.io/music/tracks/kevin-macleod/backbay-lounge',
        storage: 'org',
    },
    {
        id: 'crinoline-dreams',
        name: 'Crinoline Dreams',
        artist: 'Kevin MacLeod',
        path: '/music/uppbeat/crinoline-dreams.mp3',
        duration: 246.465,
        bpm: 100,
        mood: 'warm and uplifting',
        profile: {
            description: 'Crinoline Dreams has a warm, lightly buoyant feel, pairing a smooth melodic texture with a steady, moderate pulse. It feels relaxed without becoming sleepy, making it a natural companion for skincare, beauty appointments, nail services, or polished salon content. Try it in a Skin Edit with product details and finishing shots, or a Step by Step sequence with clear, measured cuts. Its gentle lift also suits understated promotions and approachable educational voiceover, with the music kept below the spoken explanation.',
            mood: 'warm and uplifting',
            genre: 'acoustic pop',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'The instrumental nature and steady rhythm would allow voiceover to sit comfortably over the track without competing for attention.',
            serviceUses: [
                'Facials and skincare',
                'Nail services',
                'Hair and beauty',
                'Beauty product demonstrations',
            ],
            videoUses: [
                'Skin Edit',
                'Step by Step',
                'Educational voiceover',
                'Understated promotions',
            ],
            editingNotes: 'Smooth transitions and cuts in sync with the steady beat will feel natural. A fade-in at the start and gentle fade-out at the end can work well. Keep voiceovers clear and slightly louder than the music.',
            recommendedStartSec: 0,
        },
        audioSha256: '433ad0565b62f9905bcb06b566e88b9564257a08ee1294cbcf7b64cda50d830c',
        sourceUrl: 'https://uppbeat.io/music/tracks/kevin-macleod/crinoline-dreams',
        storage: 'org',
    },
    {
        id: 'shades-of-spring',
        name: 'Shades Of Spring',
        artist: 'Kevin MacLeod',
        path: '/music/uppbeat/shades-of-spring.mp3',
        duration: 187.376,
        bpm: 96,
        mood: 'calm, uplifting',
        profile: {
            description: 'Shades Of Spring brings a soft, organic warmth with a relaxed but gently moving rhythm. The mood is calm and bright, useful for everyday salon stories, fresh nail looks, beauty routines, or welcoming studio introductions. A Step by Step edit can follow its steady pace, while a transformation or reveal can lean on its understated lift rather than a big musical payoff. It also fits seasonal promotions and behind-the-scenes clips, especially when the visuals feel natural, friendly, and lightly polished.',
            mood: 'calm, uplifting',
            genre: 'acoustic pop / folk',
            energy: 'medium',
            vocals: 'wordless',
            voiceoverFit: 'The instrumental arrangement leaves ample space for voiceover, making it suitable for narratives or spoken content.',
            serviceUses: [
                'Hair salon services',
                'Nail services',
                'Beauty routines',
                'Fitness studio introductions',
            ],
            videoUses: [
                'Step by Step',
                'Transformation/reveal',
                'Seasonal promotions',
                'Behind-the-scenes clips',
            ],
            editingNotes: 'The consistent rhythm lends itself to smooth transitions and moderate pacing of cuts. Subtle builds in the arrangement can be highlighted for emotional emphasis.',
            recommendedStartSec: 0,
        },
        audioSha256: '9684fa999e4a142f3fdb999addbc32ad7f475cbe5b03703cb282b9060110b554',
        sourceUrl: 'https://uppbeat.io/music/tracks/kevin-macleod/shades-of-spring',
        storage: 'org',
    },
    {
        id: 'into-the-deep',
        name: 'Into The Deep',
        artist: 'Otto.mp3',
        path: '/music/uppbeat/into-the-deep.mp3',
        duration: 107.494,
        bpm: 70,
        mood: 'peaceful and introspective',
        profile: {
            description: 'Into The Deep is a spacious, piano-led track with delicate, shimmering accents and a slow, gentle pace. Its peaceful, reflective mood suits close-up skincare footage, massage room details, wellness studio stories, or quietly presented clinical services. Choose it for a Skin Edit that gives each image room to breathe, or a Journey built around atmosphere and careful service details. The sparse texture also supports educational voiceover. Slow cuts, soft fades, and restrained reveals feel more at home here than energetic promotional editing.',
            mood: 'peaceful and introspective',
            genre: 'ambient piano / instrumental',
            energy: 'low',
            vocals: 'none',
            voiceoverFit: 'well-suited for voiceovers due to minimal instrumentation',
            serviceUses: [
                'Facials and skincare',
                'Massage',
                'Wellness studio services',
                'Clinical service introductions',
            ],
            videoUses: [
                'Skin Edit',
                'Journey',
                'Educational voiceover',
                'Atmospheric service showcases',
            ],
            editingNotes: "The track's simplicity allows easy editing for fade-ins and fade-outs. Consider using soft transitions and slow-motion clips to match the pace.",
            recommendedStartSec: 0,
        },
        audioSha256: '58db394938a845155f70e254ab4ddcd92b5b7b46265111d67997653347b133f2',
        sourceUrl: 'https://uppbeat.io/music/tracks/ottomp3/into-the-deep',
        storage: 'org',
    },
    {
        id: 'coming-awake',
        name: 'Coming Awake',
        artist: 'Dan Barton',
        path: '/music/uppbeat/coming-awake.mp3',
        duration: 77.375,
        bpm: 85,
        mood: 'calm and uplifting',
        profile: {
            description: "Coming Awake offers a light, reflective atmosphere with a steady, relaxed pulse and a gentle sense of forward movement. Its calm, uplifting character can complement a client's salon Journey, a beauty transformation, or a personal introduction from a wellness or fitness professional. Let longer shots establish the story, then use measured cuts to build toward the finished look. It is especially suited to visually led storytelling and personal branding; for spoken sections, check the balance and lower the music so the explanation remains clear.",
            mood: 'calm and uplifting',
            genre: 'acoustic pop',
            energy: 'medium',
            vocals: 'lyrics',
            voiceoverFit: 'moderate, the presence of lyrics may compete with spoken content',
            serviceUses: [
                'Hair and beauty',
                'Wellness services',
                'Fitness professional introductions',
                'Service-provider personal branding',
            ],
            videoUses: [
                'Journey',
                'Transformation/reveal',
                'Personal introduction',
                'Visual storytelling montage',
            ],
            editingNotes: 'The steady rhythm makes it easy to cut on the beat. Consider lowering the volume during voiceover sections or using instrumental portions.',
            recommendedStartSec: 0,
        },
        audioSha256: '177f5967b4a00000dce5f322b78fe43eb1b5409bcc09f5a8bf7949ec09667f03',
        sourceUrl: 'https://uppbeat.io/music/tracks/dan-barton/coming-awake',
        storage: 'org',
    },
    {
        id: 'taste-of-fresh',
        name: 'Taste Of Fresh',
        artist: 'Dope Cat',
        path: '/music/uppbeat/taste-of-fresh.mp3',
        duration: 152.007,
        bpm: 100,
        mood: 'uplifting and calm',
        profile: {
            description: 'Smooth electronic textures and a steady mid-tempo pulse give Taste Of Fresh a calm but forward-moving feel. Its modern, lightly uplifting character suits skincare, facials, contemporary hair studios, and polished beauty content without feeling too sleepy or too intense. Use it for a Journey through an appointment, a clean Skin Edit, or a product-focused Step by Step. The restrained melodic feel can support educational voiceover, while gentle cuts and smooth transitions keep the visuals feeling fresh and composed.',
            mood: 'uplifting and calm',
            genre: 'electronic/chill',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'suitable for voiceovers due to unobtrusive melody and steady rhythm',
            serviceUses: [
                'Facials and skincare',
                'Hair and beauty',
                'Nails',
                'Modern wellness studios',
            ],
            videoUses: [
                'Journey',
                'Skin Edit',
                'Step by Step',
                'Educational voiceover',
                'Product showcase',
            ],
            editingNotes: 'Smooth fades and transitions will complement the consistent beat. Looping sections can be done easily given the repetitive rhythm.',
            recommendedStartSec: 0,
        },
        audioSha256: '75bee6617d315d8cefaef8f6e848dd8b53a21e6379061ca74a94dfdfa2245a9a',
        sourceUrl: 'https://uppbeat.io/music/tracks/dope-cat/taste-of-fresh',
        storage: 'org',
    },
    {
        id: 'peace-be-with-you',
        name: 'Peace Be With You',
        artist: 'Christopher Galovan',
        path: '/music/uppbeat/peace-be-with-you.mp3',
        duration: 193.176,
        bpm: 70,
        mood: 'relaxing, introspective, and soothing',
        profile: {
            description: 'Peace Be With You brings a soft, folk-inspired warmth with an unhurried pace and an intimate, understated atmosphere. It is a natural choice for massage, wellness, gentle skincare, or welcoming clinical and dental introductions where the video needs a quieter tone. Try it beneath a reflective Journey, a softly paced Step by Step, or educational voiceover explaining what to expect at an appointment. Lingering detail shots and gentle transitions suit its low-energy character better than rapid cuts or dramatic reveals.',
            mood: 'relaxing, introspective, and soothing',
            genre: 'acoustic, folk-inspired',
            energy: 'low',
            vocals: 'none',
            voiceoverFit: 'The minimal instrumentation leaves ample space for a voiceover, making it suitable as background for spoken content.',
            serviceUses: [
                'Massage',
                'Wellness',
                'Gentle skincare',
                'Clinical and dental introductions',
            ],
            videoUses: [
                'Journey',
                'Step by Step',
                'Educational voiceover',
                'Appointment introduction',
                'Behind the scenes',
            ],
            editingNotes: "Fade-ins and fade-outs would blend smoothly. The consistent pace allows easy looping or cutting to length. Gentle cross-dissolves between clips would complement the music's softness.",
            recommendedStartSec: 0,
        },
        audioSha256: '89ef4c8a26e536071cf894055c3eaf478b7473e9a6586b085611e17d60c3444c',
        sourceUrl: 'https://uppbeat.io/music/tracks/christopher-galovan/peace-be-with-you',
        storage: 'org',
    },
    {
        id: 'chasm',
        name: 'Chasm',
        artist: 'Greenhouse',
        path: '/music/uppbeat/chasm.mp3',
        duration: 125.362,
        bpm: 120,
        mood: 'energetic and uplifting',
        profile: {
            description: 'Chasm pairs an easy indie identity with a fuller, more driving feel than a typical quiet background track. Its steady, upbeat momentum can bring personality to natural-care brands, hair services, beauty studios, and active wellness content. Choose it for a lively Journey, behind-the-scenes teamwork, or a transformation that builds toward the finished look. Rhythm-led edits can make service details feel engaging. For spoken explanations, check the chosen section and lower the music so its busier texture does not compete with the voice.',
            mood: 'energetic and uplifting',
            genre: 'rock',
            energy: 'high',
            vocals: 'lyrics',
            voiceoverFit: 'Moderate, due to prominent vocals',
            serviceUses: [
                'Natural-care brands',
                'Hair services',
                'Beauty studios',
                'Active wellness',
            ],
            videoUses: [
                'Journey',
                'Behind the scenes',
                'Transformation/reveal',
                'Brand showcase',
            ],
            editingNotes: 'Consider cutting to the rhythm of the drums for dynamic transitions. Keep voiceovers minimal to avoid clashing with vocals.',
            recommendedStartSec: 0,
        },
        audioSha256: '53e346c592edd6c1361f4ed4d18e6d7cea23760b78e5c652d19cdc61c0b7e109',
        sourceUrl: 'https://uppbeat.io/music/tracks/greenhouse/chasm',
        storage: 'org',
    },
    {
        id: 'swagger',
        name: 'Swagger',
        artist: 'Trinity',
        path: '/music/uppbeat/swagger.mp3',
        duration: 132.049,
        bpm: 120,
        mood: 'Energetic and uplifting',
        profile: {
            description: 'Swagger has a confident, punchy character, with a strong groove and a brisk, energetic pace. It suits bold hair transformations, expressive nail art, beauty launches, and fitness highlights where attitude is part of the presentation. Use it for a sharp transformation or reveal, a promotion, or a team montage with personality. Match cuts to the rhythmic accents and let the finished result hold the spotlight. Keep spoken passages brief or lower the track beneath narration, since the fuller sound may compete with detailed explanations.',
            mood: 'Energetic and uplifting',
            genre: 'Rock/Pop Rock',
            energy: 'high',
            vocals: 'lyrics',
            voiceoverFit: 'Moderate - strong instrumentation might compete with narration, but quieter sections could work for voiceovers.',
            serviceUses: [
                'Hair transformations',
                'Nail art',
                'Beauty launches',
                'Fitness',
            ],
            videoUses: [
                'Transformation/reveal',
                'Promotions',
                'Team montage',
                'Fitness highlights',
            ],
            editingNotes: 'Match energetic cuts to guitar riffs and drum hits; consider a brief fade during quieter segments for voiceover clarity.',
            recommendedStartSec: 0,
        },
        audioSha256: 'e8d34cd496bfba271d941b135e9d327cfd9ab7d39fc5b6edca756082a2cd7fe4',
        sourceUrl: 'https://uppbeat.io/music/tracks/trinity/swagger',
        storage: 'org',
    },
    {
        id: 'groove-sauce',
        name: 'Groove Sauce',
        artist: 'Stan Town',
        path: '/music/uppbeat/groove-sauce.mp3',
        duration: 116.036,
        bpm: 124,
        mood: 'energetic and uplifting',
        profile: {
            description: 'Groove Sauce offers a stylish electronic groove with a bright, upbeat feel and a quick, steady dance pulse. It works well for trend-led hair and beauty, detailed nail services, fitness studios, and product-led skincare content. Try a rhythmic Step by Step, a glossy Skin Edit, or a promotion built around crisp detail shots and a polished reveal. Its consistent momentum helps connect short clips, while the relatively open arrangement can support concise voiceover when the music is kept comfortably below the speaker.',
            mood: 'energetic and uplifting',
            genre: 'electronic dance / house',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'Good fit for voiceover as the instrumental leaves space and avoids lyrical distractions.',
            serviceUses: [
                'Hair and beauty',
                'Nails',
                'Fitness studios',
                'Product-led skincare',
            ],
            videoUses: [
                'Step by Step',
                'Skin Edit',
                'Promotions',
                'Transformation/reveal',
                'Short educational voiceover',
            ],
            editingNotes: 'Use the steady beat for cutting transitions; emphasize the drops to highlight key visuals or messages. Loopable sections around the main chorus for extended use.',
            recommendedStartSec: 0,
        },
        audioSha256: '1ca18a5d11b7a9fab321bbb45b67cc2048f875a7bace4acbd4060b48fb02dcc7',
        sourceUrl: 'https://uppbeat.io/music/tracks/stan-town/groove-sauce',
        storage: 'org',
    },
    {
        id: 'thrive',
        name: 'Thrive',
        artist: 'All Good Folks',
        path: '/music/uppbeat/thrive.mp3',
        duration: 85.551,
        bpm: 120,
        mood: 'uplifting and inspirational',
        profile: {
            description: 'Thrive pairs a driving beat with bright electronic textures and a broad cinematic lift. Its upbeat, high-energy feel suits videos that build toward a standout finish, from hair and beauty transformations to fitness highlights or a polished skincare reveal. Use it for a Journey with momentum, a dramatic transformation/reveal, or a promotion with confident visual pacing. Let the musical lift frame the finished look, and lower the track beneath narration so its fuller sound does not compete with the message.',
            mood: 'uplifting and inspirational',
            genre: 'cinematic electronic',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'The energetic yet cinematic vibe may compete with voiceovers, but could still support motivational narration if levels are adjusted.',
            serviceUses: ['Hair and beauty', 'Facials and skincare', 'Fitness'],
            videoUses: ['Journey', 'Transformation/reveal', 'Promotions'],
            editingNotes: 'Allow the strong beat to guide quick cuts or dynamic transitions. The orchestral swell can be aligned with key visual highlights.',
            recommendedStartSec: 0,
        },
        audioSha256: '66c8c8bfac72a7b72e80b299ad44500d22f46087f525e874d5c2a6991aa43031',
        sourceUrl: 'https://uppbeat.io/music/tracks/all-good-folks/thrive',
        storage: 'org',
    },
    {
        id: 'get-your-groove-on',
        name: 'Get Your Groove On',
        artist: 'All Good Folks',
        path: '/music/uppbeat/get-your-groove-on.mp3',
        duration: 90.54,
        bpm: 124,
        mood: 'uplifting and energetic',
        profile: {
            description: 'Get Your Groove On brings a disco-leaning dance feel, a pulsing groove, and bright electronic layers to fast, upbeat edits. The steady pace gives hair transformations, nail art, beauty styling, and fitness clips a playful sense of movement. Try it for a transformation/reveal, a lively Step by Step, or a promotion built around bold details and quick changes. Cut on the beat to connect stages, and choose a less busy passage when a short voiceover needs room.',
            mood: 'uplifting and energetic',
            genre: 'electronic dance / house',
            energy: 'high',
            vocals: 'wordless',
            voiceoverFit: 'The track provides enough space in the mix for a voiceover, especially during breakdown sections.',
            serviceUses: ['Hair and beauty', 'Nails', 'Fitness'],
            videoUses: ['Transformation/reveal', 'Step by Step', 'Promotions'],
            editingNotes: 'Harmony and rhythm remain consistent so loop edits are straightforward. Use breakdown sections for transitions or voiceovers.',
            recommendedStartSec: 0,
        },
        audioSha256: '04447a1b333b1ee1404a96142822454d1c5eac0ef65410e73c8edc87edd53da3',
        sourceUrl: 'https://uppbeat.io/music/tracks/all-good-folks/get-your-groove-on',
        storage: 'org',
    },
    {
        id: 'high-roller',
        name: 'High Roller',
        artist: 'Stan Town',
        path: '/music/uppbeat/high-roller.mp3',
        duration: 129.306,
        bpm: 110,
        mood: 'Energetic and positive',
        profile: {
            description: 'High Roller has a punchy guitar-led sound, a firm rhythmic backing, and a bright, positive pop-rock feel. Its driving but manageable pace works for confident service introductions, salon activity, fitness demonstrations, or an upbeat look around a clinical or dental practice. Choose it for promotions, a practical Step by Step, or educational voiceover that needs energy rather than drama. The consistent groove supports clear scene changes, while keeping the music low allows spoken explanations and on-screen instructions to lead.',
            mood: 'Energetic and positive',
            genre: 'Pop rock',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'Good fit for voiceover, as the instrumentation leaves space for narration.',
            serviceUses: ['Hair and beauty', 'Fitness', 'Clinical and dental'],
            videoUses: ['Promotions', 'Step by Step', 'Educational voiceover'],
            editingNotes: 'The track maintains consistent energy throughout, making it suitable for upbeat montage sequences. The guitar-driven sections can align with scene changes. The instrumentation provides a clear backdrop for voiceover or title overlays.',
            recommendedStartSec: 0,
        },
        audioSha256: '36c4e66d1f126b183f4331cb83220e2f82ad8aca87f32f3125e8d2f3b7b26e89',
        sourceUrl: 'https://uppbeat.io/music/tracks/stan-town/high-roller',
        storage: 'org',
    },
    {
        id: 'get-it-going',
        name: 'Get It Going',
        artist: 'Stan Town',
        path: '/music/uppbeat/get-it-going.mp3',
        duration: 111.856,
        bpm: 123,
        mood: 'uplifting and upbeat',
        profile: {
            description: 'Get It Going offers a crisp electronic beat, bright melodic layers, and a polished, upbeat mood. Its quick, consistent pace is especially useful for montage sequences that move through several service stages without lingering. Pair it with busy salon footage, nail preparation and finishing details, or fitness session highlights. It suits a brisk Journey, a tightly edited Step by Step, and short promotions. Match cuts to the rhythm, and ease the volume down when adding concise educational voiceover.',
            mood: 'uplifting and upbeat',
            genre: 'electronic pop/dance',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'Fits well under a voiceover, as there are no vocals and the mix leaves room for speech.',
            serviceUses: ['Hair and beauty', 'Nails', 'Fitness'],
            videoUses: [
                'Journey',
                'Step by Step',
                'Promotions',
                'Educational voiceover',
            ],
            editingNotes: 'Cut footage to the steady beat for a dynamic feel; transitions can be aligned with percussive hits to maintain energy.',
            recommendedStartSec: 0,
        },
        audioSha256: '1ba12059e2311a5da44c57b2c2d9fc2ea4444856842481ec59a91d94bf8a6c31',
        sourceUrl: 'https://uppbeat.io/music/tracks/stan-town/get-it-going',
        storage: 'org',
    },
    {
        id: 'the-flower',
        name: 'The Flower',
        artist: 'RA',
        path: '/music/uppbeat/the-flower.mp3',
        duration: 130.273,
        bpm: 90,
        mood: 'smooth and relaxed with a hint of sophistication',
        profile: {
            description: 'The Flower settles into a warm, soul-influenced groove with mellow layers and a relaxed, moderate pace. The mood is polished and quietly confident, making it a natural backdrop for facials and skincare, massage and wellness settings, or unhurried beauty details. Use it for a Skin Edit, a gently paced Journey, or behind-the-scenes footage that gives the space and process time to breathe. Smooth transitions suit its easy rhythm. For educational voiceover, check the chosen passage for any competing vocal content.',
            mood: 'smooth and relaxed with a hint of sophistication',
            genre: 'R&B / chilled pop',
            energy: 'medium',
            vocals: 'lyrics',
            voiceoverFit: 'fits well under voiceover due to consistent groove and space in arrangement',
            serviceUses: [
                'Facials and skincare',
                'Massage and wellness',
                'Hair and beauty',
            ],
            videoUses: [
                'Skin Edit',
                'Journey',
                'Behind the scenes',
                'Educational voiceover',
            ],
            editingNotes: 'Use smooth transitions and possibly slow-motion cuts to match the relaxed pace. Emphasize moments where the beat is clear for scene changes.',
            recommendedStartSec: 0,
        },
        audioSha256: '352622061bcef54ade59ca58366bb6caeb648a681ffc42e1ba3821b559e42aa1',
        sourceUrl: 'https://uppbeat.io/music/tracks/ra/the-flower',
        storage: 'org',
    },
    {
        id: 'fat-funk',
        name: 'Fat Funk',
        artist: 'Matrika',
        path: '/music/uppbeat/fat-funk.mp3',
        duration: 140.042,
        bpm: 95,
        mood: 'energetic and uplifting',
        profile: {
            description: 'Bold, funky electronic energy gives this track a punchy, confident feel, with a weighty low end and a repeating groove that keeps edits moving. The pace feels bouncy rather than frantic, making it a strong option for hair and beauty transformations, expressive nail designs, fitness clips, or product promotions. Try it for a transformation/reveal, a fast Step by Step, or a lively behind-the-scenes reel. For educational voiceover, choose a less busy passage and check that any vocal elements do not compete with speech.',
            mood: 'energetic and uplifting',
            genre: 'electronic pop / hip-hop',
            energy: 'high',
            vocals: 'lyrics',
            voiceoverFit: 'The instrumental sections would allow voiceovers to fit well, though the presence of vocals may compete at times.',
            serviceUses: [
                'Hair and beauty',
                'Nails and nail art',
                'Fitness',
                'Beauty product showcases',
            ],
            videoUses: [
                'Transformation/reveal',
                'Fast Step by Step',
                'Behind-the-scenes reels',
                'Promotions',
            ],
            editingNotes: 'Cut at the drops or instrumental sections for seamless loops. Emphasize transitions on the beat for dynamic edits.',
            recommendedStartSec: 0,
        },
        audioSha256: '6e0a9a7ec75d886504341e47057def7174c9b12ffc5a43412b12ed331d95d9b6',
        sourceUrl: 'https://uppbeat.io/music/tracks/matrika/fat-funk',
        storage: 'org',
    },
    {
        id: 'check-it-out',
        name: 'Check It Out',
        artist: 'Mountaineer',
        path: '/music/uppbeat/check-it-out.mp3',
        duration: 124.186,
        bpm: 120,
        mood: 'uplifting and confident',
        profile: {
            description: 'A crisp, upbeat groove gives this track an easy confidence, pairing a steady beat with clean, looping electronic textures. It feels brisk and approachable without demanding a dramatic storyline. Consider it for nail appointments, hair styling, skincare product demonstrations, or fitness technique clips. Short Step by Step videos, quick Skin Edit sequences, and service promotions can all follow its consistent pulse. It also suits concise educational voiceover, with the music kept low enough for instructions to remain clear and easy to follow.',
            mood: 'uplifting and confident',
            genre: 'electronic / dance',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'good for clear voiceovers under energetic content',
            serviceUses: ['Nails', 'Hair styling', 'Facials and skincare', 'Fitness'],
            videoUses: [
                'Short Step by Step',
                'Skin Edit',
                'Educational voiceover',
                'Service promotions',
            ],
            editingNotes: 'Loopable sections make it easy for seamless transitions. The consistent beat supports quick cuts and dynamic visual changes.',
            recommendedStartSec: 0,
        },
        audioSha256: '1cd30f74cf592c76ae4bdf68681a8d9aaca0f6a9156622bcfc288828987053a8',
        sourceUrl: 'https://uppbeat.io/music/tracks/mountaineer/check-it-out',
        storage: 'org',
    },
    {
        id: 'neverending-story',
        name: 'Neverending Story',
        artist: 'Iros Young',
        path: '/music/uppbeat/neverending-story.mp3',
        duration: 146.26,
        bpm: 80,
        mood: 'inspirational and uplifting',
        profile: {
            description: 'This uplifting cinematic track moves from a gentle opening toward a broad, powerful finish, creating a clear sense of anticipation and arrival. Its measured pace and expanding orchestral texture suit videos that need an emotional arc rather than constant quick cuts. Consider it for a bridal hair transformation, a carefully paced facial Journey, or a fitness milestone story. Place introductory educational voiceover over the quieter opening, then let a transformation/reveal or a major brand promotion coincide with the stronger musical build.',
            mood: 'inspirational and uplifting',
            genre: 'cinematic orchestral',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'The structure and instrumentation leave space for voiceover, especially in the calmer intro sections.',
            serviceUses: [
                'Bridal hair and beauty',
                'Facials and skincare',
                'Fitness',
                'Service-business branding',
            ],
            videoUses: [
                'Journey',
                'Transformation/reveal',
                'Introductory educational voiceover',
                'Brand promotions',
            ],
            editingNotes: 'Use the subtle intro for calmer visuals, then synchronize dramatic crescendos with key visual moments. The dynamic build lends itself well to arc-based storytelling.',
            recommendedStartSec: 0,
        },
        audioSha256: '7c41196d6bca122e631c5a326a2738be9673aa6a6f62765174b36c8c1b2a5f19',
        sourceUrl: 'https://uppbeat.io/music/tracks/iros-young/neverending-story',
        storage: 'org',
    },
    {
        id: 'the-grand-hallway',
        name: 'The Grand Hallway',
        artist: 'Simon Folwar',
        path: '/music/uppbeat/the-grand-hallway.mp3',
        duration: 148.741,
        bpm: 120,
        mood: 'uplifting and optimistic',
        profile: {
            description: 'Flowing classical textures and a steady pulse give this track a polished, editorial character. The mood is optimistic and composed, with enough movement to support a sequence without turning it into a dramatic reveal. It could complement premium skincare, massage and wellness spaces, refined hair styling, or a clinical or dental practice introduction. Use it for a considered Skin Edit, an orderly Step by Step, or educational voiceover. Its moderate energy also suits a Journey focused on details, atmosphere, and the appointment experience.',
            mood: 'uplifting and optimistic',
            genre: 'contemporary classical / cinematic',
            energy: 'medium',
            vocals: 'none',
            voiceoverFit: 'Works well under a voiceover as the melody is clear but not overpowering.',
            serviceUses: [
                'Facials and skincare',
                'Massage and wellness',
                'Hair styling',
                'Clinical and dental practice introductions',
            ],
            videoUses: [
                'Skin Edit',
                'Step by Step',
                'Educational voiceover',
                'Journey',
            ],
            editingNotes: 'The track has a steady rhythm suitable for pacing transitions. It can be looped if needed. The dynamic range is moderate, allowing easy application for background use. Consider starting at the introduction for a calm build-up.',
            recommendedStartSec: 0,
        },
        audioSha256: '248f7189f4b39bb6bb7c270ee9961533723a2f020553502dd491c8f8a4564f5b',
        sourceUrl: 'https://uppbeat.io/music/tracks/simon-folwar/the-grand-hallway',
        storage: 'org',
    },
    {
        id: 'beautiful-wonder',
        name: 'Beautiful Wonder',
        artist: 'Aylex',
        path: '/music/uppbeat/beautiful-wonder.mp3',
        duration: 143.125,
        bpm: 80,
        mood: 'dramatic and inspiring',
        profile: {
            description: 'Atmospheric and inspiring, this orchestral track builds gradually into a large cinematic swell. Its measured momentum and rich layers make it better suited to an unfolding story than a snappy demonstration. Try it for a spa or wellness venue Journey, a salon brand story, or a fitness event recap, using the build to connect preparation, activity, and the closing moment. Quieter passages can support narration, while the fuller sections lend scale to a transformation/reveal, an opening announcement, or a milestone promotion.',
            mood: 'dramatic and inspiring',
            genre: 'cinematic orchestral',
            energy: 'high',
            vocals: 'none',
            voiceoverFit: 'Good for voiceover - the music swells but has sections for narration.',
            serviceUses: [
                'Spa and wellness venues',
                'Hair and beauty salons',
                'Fitness events',
                'Service-business launches',
            ],
            videoUses: [
                'Journey',
                'Brand storytelling',
                'Event recaps',
                'Transformation/reveal',
                'Opening and milestone promotions',
            ],
            editingNotes: 'Use the build-ups as transitions; align visuals with the crescendos for maximum impact. There are quieter sections that can be ideal for voiceovers or key messages.',
            recommendedStartSec: 0,
        },
        audioSha256: 'c7fb07236c8245ebf5d87eec90790984031b47d2b1783a255e2e9bb284de467f',
        sourceUrl: 'https://uppbeat.io/music/tracks/aylex/beautiful-wonder',
        storage: 'org',
    },
    {
        id: 'dreamstate',
        name: 'Dreamstate',
        artist: 'Paul Yudin',
        path: '/music/uppbeat/dreamstate.mp3',
        duration: 123.533,
        bpm: 120,
        mood: 'uplifting and positive',
        profile: {
            description: 'Dreamstate pairs a cinematic electronic atmosphere with a clear, steady pulse and bright melodic layers. It feels uplifting and polished rather than purely restful, making it a useful option for hair and beauty, nails, skincare, or measured fitness content. Try it for a Journey with forward momentum, a Skin Edit with rhythmic detail shots, or a transformation reveal and promotion. Keep cuts deliberate and let key visual changes meet the beat, leaving room for narration when needed.',
            mood: 'uplifting and positive',
            genre: 'electronic pop',
            energy: 'medium',
            vocals: 'wordless',
            voiceoverFit: 'The instrumental quality and steady rhythm make it good for voiceovers without clashing.',
            serviceUses: [
                'Hair and beauty',
                'Nails',
                'Facials and skincare',
                'Fitness',
            ],
            videoUses: [
                'Journey',
                'Skin Edit',
                'Transformation/reveal',
                'Promotions',
            ],
            editingNotes: 'Highlight transitions at the beat changes and use the steady rhythm for cuts. The melodic synth line can be synced with key visual moments.',
            recommendedStartSec: 0,
        },
        audioSha256: 'c13e47a029396c506466373c45ad8fbfe6accb954815a335475e66a69b11d5e1',
        sourceUrl: 'https://uppbeat.io/music/tracks/paul-yudin/dreamstate',
        storage: 'org',
    },
    {
        id: 'living-waters',
        name: 'Living Waters',
        artist: 'Brock Hewitt: Stories in Sound',
        path: '/music/uppbeat/living-waters.mp3',
        duration: 266.057,
        bpm: 60,
        mood: 'peaceful, reflective, comforting',
        profile: {
            description: 'Living Waters offers warm, gently flowing piano at an unhurried pace, with a peaceful and comforting mood. Its simple melodic shape suits massage and wellness settings, facial appointments, and softly presented clinical or dental introductions. Use it beneath educational voiceover, a calm Step by Step, or a Journey focused on the appointment experience. Slow crossfades and lingering detail shots complement the music, while the understated arrangement gives spoken explanations space without making the edit feel empty.',
            mood: 'peaceful, reflective, comforting',
            genre: 'solo piano / classical crossover',
            energy: 'low',
            vocals: 'none',
            voiceoverFit: 'excellent background for narration or voiceover because it doesn’t compete for attention',
            serviceUses: [
                'Massage and wellness',
                'Facials and skincare',
                'Clinical and dental',
            ],
            videoUses: ['Educational voiceover', 'Step by Step', 'Journey'],
            editingNotes: 'Use as a subtle underscore during calm scenes or transitions. Fade in gently at the start and fade out at the end. Works well with slow crossfades and minimal transitions.',
            recommendedStartSec: 0,
        },
        audioSha256: '2db0bf23d4021c016e31d1cc1605da6d2b1046d0329a19b9df2160c5071697ae',
        sourceUrl: 'https://uppbeat.io/music/tracks/brock-hewitt-stories-in-sound/living-waters',
        storage: 'org',
    },
    {
        id: 'skylark',
        name: 'Skylark',
        artist: 'Oliver Massa',
        path: '/music/uppbeat/skylark.mp3',
        duration: 180.01,
        bpm: 75,
        mood: 'calm, introspective',
        profile: {
            description: 'Skylark has a dreamy, reflective piano sound, combining soft chords with a gently moving melody and a relaxed, steady pace. The intimate feel lends itself to skincare, considered hair and beauty work, or close-up nail artistry where the details carry the story. Choose it for a delicate Skin Edit, behind-the-scenes Journey, or an understated transformation reveal. Smooth transitions and natural pauses can give finished results a little breathing room, while its light texture also supports a thoughtful brand voiceover.',
            mood: 'calm, introspective',
            genre: 'classical, piano instrumental',
            energy: 'low',
            vocals: 'none',
            voiceoverFit: 'excellent for voiceover, because the piano accompaniment does not overpower narration',
            serviceUses: ['Facials and skincare', 'Hair and beauty', 'Nail artistry'],
            videoUses: [
                'Skin Edit',
                'Journey',
                'Transformation/reveal',
                'Brand storytelling voiceover',
            ],
            editingNotes: 'Use the sustained piano tones for smooth transitions. Let the natural pauses in the music underscore key branding or narrative points.',
            recommendedStartSec: 0,
        },
        audioSha256: '2398ca0c7899afb260e715aa460058c98f1d3bc2874e63baa96fd06953f7897c',
        sourceUrl: 'https://uppbeat.io/music/tracks/oliver-massa/skylark',
        storage: 'org',
    },
    {
        id: 'without-amity-fall',
        name: 'Without (Amity Fall)',
        artist: 'Sonder House',
        path: '/music/uppbeat/without-amity-fall.mp3',
        duration: 147.357,
        bpm: 60,
        mood: 'peaceful, reflective, and soothing',
        profile: {
            description: 'Without (Amity Fall) is quiet and intimate, with gentle piano lines, a sparse ambient backdrop, and a slow, even pace. Its reflective character works well for massage and wellness, facial care, or personal beauty stories that need a restrained presentation. Use it for a softly narrated client Journey, a testimonial, or a Step by Step built around careful preparation and small details. Gentle fades and longer shots fit better than rapid cuts, keeping the focus on the person and process.',
            mood: 'peaceful, reflective, and soothing',
            genre: 'instrumental piano / ambient',
            energy: 'low',
            vocals: 'none',
            voiceoverFit: 'Excellent for voiceover due to minimal instrumentation and calm dynamics.',
            serviceUses: [
                'Massage and wellness',
                'Facials and skincare',
                'Personal beauty services',
            ],
            videoUses: ['Narrated Journey', 'Client testimonials', 'Step by Step'],
            editingNotes: 'Use slow fades and smooth transitions to complement the gentle pace. Voiceover can be added without clashing. Good for segments requiring emotional introspection.',
            recommendedStartSec: 0,
        },
        audioSha256: '70943d71805741f04dda9a637aff8a1dde2975571fee94057580adda692061cb',
        sourceUrl: 'https://uppbeat.io/music/tracks/sonder-house/without-amity-fall',
        storage: 'org',
    },
    {
        id: 'cambridge-in-autumn-ennio-campbell',
        name: 'Cambridge In Autumn (Ennio Campbell)',
        artist: 'Waterway Music',
        path: '/music/uppbeat/cambridge-in-autumn-ennio-campbell.mp3',
        duration: 109.923,
        bpm: 60,
        mood: 'peaceful, reflective',
        profile: {
            description: 'Cambridge In Autumn (Ennio Campbell) brings soft, airy piano and a consistent slow rhythm to a calm, considered edit. The light arrangement is especially useful for consultation-led content in skincare, clinical or dental services, and hair and beauty, where spoken information matters. Pair it with educational voiceover, a consultation Journey, or a clear Step by Step explaining an appointment or routine. Keep transitions gentle and visuals easy to follow, letting the music support the explanation rather than drive a dramatic reveal.',
            mood: 'peaceful, reflective',
            genre: 'classical/ambient piano',
            energy: 'low',
            vocals: 'none',
            voiceoverFit: "This track's minimal arrangement leaves plenty of sonic space for a speaking voice, making it suitable for voiceover.",
            serviceUses: [
                'Skincare consultations',
                'Clinical and dental',
                'Hair and beauty consultations',
            ],
            videoUses: [
                'Educational voiceover',
                'Consultation Journey',
                'Step by Step',
            ],
            editingNotes: 'Use gentle transitions and fade-ins/outs. Keep the visuals slow-paced and serene to match the track’s feel.',
            recommendedStartSec: 0,
        },
        audioSha256: '8e7c74ac3e1ff7dff1d7f9b52153e09ff2701c1429693b7351323ac7c3a64a88',
        sourceUrl: 'https://uppbeat.io/music/tracks/waterway-music/cambridge-in-autumn-ennio-campbell',
        storage: 'org',
    },
];
//# sourceMappingURL=uppbeat-music.js.map